import { format, isSameDay, isSameMonth } from 'date-fns';
import { memo, useCallback } from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { createStyles, useScaling } from '@/hooks';
import FamilyCalendarEventChip from './event-chip';

const MAX_EVENT_SLOTS = 2;

type FamilyCalendarDayCellProps = {
  day: Date;
  month: Date;
  selectedDate: Date;
  today: Date;
  events: FamilyEvent[];
  onSelectDay: (day: Date) => void;
};

function FamilyCalendarDayCell({
  day,
  month,
  selectedDate,
  today,
  events,
  onSelectDay,
}: FamilyCalendarDayCellProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const isOutsideMonth = !isSameMonth(day, month);
  const isSelected = !isOutsideMonth && isSameDay(day, selectedDate);
  const isCurrentDay = !isOutsideMonth && isSameDay(day, today);
  const shouldSummarizeEvents = events.length > MAX_EVENT_SLOTS;
  const visibleEvents = shouldSummarizeEvents ? events.slice(0, 1) : events;
  const overflowCount = shouldSummarizeEvents ? events.length - 1 : 0;

  const handlePress = useCallback(() => onSelectDay(day), [day, onSelectDay]);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={STRINGS.guardian.kids.selectDate.replace(
        '{date}',
        format(day, 'MMMM d, yyyy'),
      )}
      accessibilityState={{ selected: isSelected, disabled: isOutsideMonth }}
      disabled={isOutsideMonth}
      onPress={handlePress}
      style={({ pressed }) => [styles.cell, pressed ? styles.pressed : null]}>
      {!isOutsideMonth ? (
        <>
          <Stack align="center">
            <Stack
              width={scale(24)}
              height={scale(24)}
              borderRadius={scale(6)}
              align="center"
              justify="center"
              style={[isCurrentDay ? styles.today : null, isSelected ? styles.selectedDay : null]}>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
                style={isSelected ? styles.selectedDayText : styles.dayText}>
                {format(day, 'd')}
              </Text>
            </Stack>
          </Stack>

          <Stack gap={scale(1)} marginTop={scale(2)}>
            {visibleEvents.map(event => (
              <FamilyCalendarEventChip key={event.id} event={event} />
            ))}
            {overflowCount > 0 ? (
              <Stack
                borderRadius={scale(4)}
                paddingX={scale(4)}
                paddingY={scale(2)}
                gap={scale(10)}
                justify="center"
                style={styles.moreChip}>
                <Text
                  numberOfLines={1}
                  fontFamily="OpenSans"
                  fontSize={scale(8)}
                  lineHeight={scale(12)}
                  fontWeight={'400'}
                  style={styles.moreText}>
                  {STRINGS.guardian.kids.moreEvents.replace('{count}', String(overflowCount))}
                </Text>
              </Stack>
            ) : null}
          </Stack>
        </>
      ) : null}
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  cell: {
    flex: 1,
    paddingTop: scale(4),
    paddingBottom: scale(3),
    paddingHorizontal: scale(4),
    overflow: 'hidden',
  },
  pressed: {
    opacity: 0.8,
  },
  dayText: {
    color: '#252525',
    textAlign: 'center',
  },
  selectedDay: {
    backgroundColor: '#0033A1',
    borderRadius: scale(4),
  },
  selectedDayText: {
    color: '#FFFFFF',
    textAlign: 'center',
  },
  today: {
    borderWidth: 1,
    borderColor: '#346AE1',
  },
  moreChip: {
    backgroundColor: '#0033A1',
  },
  moreText: {
    color: '#FFFFFF',
  },
}));

export default memo(FamilyCalendarDayCell);
